import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Upload, Trash2, FileText, Save, AlertTriangle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";

/**
 * Lease Agreement Editor.
 *
 * Lets a landlord either:
 *   (a) Upload an attorney-drafted custom PDF that fully replaces the auto-
 *       generated lease agreement, or
 *   (b) Override individual clauses (Permitted Use, Maintenance, Insurance,
 *       Default; Remedies, etc.) with custom HTML text. Empty/blank fields
 *       fall back to the system default for that clause.
 *
 * Uses backend endpoints:
 *   GET    /api/leases/{id}/agreement                — load state + clause list
 *   PUT    /api/leases/{id}/agreement/clauses        — save overrides
 *   POST   /api/leases/{id}/agreement/upload         — upload custom PDF
 *   DELETE /api/leases/{id}/agreement/upload         — remove custom PDF
 *   GET    /api/leases/{id}/agreement/pdf            — preview/download PDF
 */
export default function LeaseAgreementEditorDialog({ lease, onClose, onUpdated }) {
  const open = !!lease;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [state, setState] = useState(null); // { mode, custom_pdf_filename, custom_pdf_uploaded_at, clause_overrides, overridable_clauses }
  const [drafts, setDrafts] = useState({}); // local edits keyed by clause_id
  const [activeTab, setActiveTab] = useState("clauses"); // "clauses" | "upload"
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!lease) {
      setState(null);
      setDrafts({});
      setActiveTab("clauses");
      return;
    }
    loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lease?.id]);

  async function loadState() {
    if (!lease) return;
    setLoading(true);
    try {
      const r = await api.get(`/leases/${lease.id}/agreement`);
      setState(r.data);
      setDrafts({ ...(r.data.clause_overrides || {}) });
      setActiveTab(r.data.mode === "uploaded" ? "upload" : "clauses");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not load agreement state");
    } finally {
      setLoading(false);
    }
  }

  const overridableClauses = state?.overridable_clauses || [];
  const dirty = state
    ? overridableClauses.some((c) => (drafts[c.id] || "") !== (state.clause_overrides?.[c.id] || ""))
    : false;

  async function saveClauseOverrides() {
    if (!lease) return;
    setSaving(true);
    try {
      // Send the full set so we capture both edits and clears.
      const overrides = {};
      for (const c of overridableClauses) {
        overrides[c.id] = (drafts[c.id] || "").trim();
      }
      const r = await api.put(`/leases/${lease.id}/agreement/clauses`, { overrides });
      toast.success("Clause overrides saved");
      setState((prev) =>
        prev
          ? {
              ...prev,
              clause_overrides: r.data.clause_overrides || {},
              mode: r.data.clause_overrides && Object.keys(r.data.clause_overrides).length
                ? (prev.custom_pdf_filename ? "uploaded" : "overridden")
                : (prev.custom_pdf_filename ? "uploaded" : "auto"),
            }
          : prev,
      );
      setDrafts({ ...(r.data.clause_overrides || {}) });
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save overrides");
    } finally {
      setSaving(false);
    }
  }

  function clearClause(id) {
    setDrafts((d) => ({ ...d, [id]: "" }));
  }

  function revertClause(id) {
    setDrafts((d) => ({ ...d, [id]: state?.clause_overrides?.[id] || "" }));
  }

  async function onUploadPdf(e) {
    const f = e.target.files?.[0];
    if (!f || !lease) return;
    if (!f.name.toLowerCase().endsWith(".pdf") && f.type !== "application/pdf") {
      toast.error("Only PDF files are supported");
      e.target.value = "";
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10 MB)");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/leases/${lease.id}/agreement/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Custom lease PDF uploaded");
      setState((prev) =>
        prev
          ? {
              ...prev,
              mode: "uploaded",
              custom_pdf_filename: r.data.custom_pdf_filename,
              custom_pdf_uploaded_at: r.data.custom_pdf_uploaded_at,
            }
          : prev,
      );
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function removeCustomPdf() {
    if (!lease) return;
    if (!confirm("Remove the uploaded custom lease PDF? The system will fall back to the auto-generated agreement (with any clause overrides).")) return;
    try {
      await api.delete(`/leases/${lease.id}/agreement/upload`);
      toast.success("Custom PDF removed");
      setState((prev) => {
        if (!prev) return prev;
        const hasOverrides = Object.keys(prev.clause_overrides || {}).length > 0;
        return {
          ...prev,
          mode: hasOverrides ? "overridden" : "auto",
          custom_pdf_filename: null,
          custom_pdf_uploaded_at: null,
        };
      });
      setActiveTab("clauses");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not remove PDF");
    }
  }

  async function previewPdf() {
    if (!lease) return;
    try {
      toast.loading("Generating preview…", { id: `agreement-preview-${lease.id}` });
      const safeProp = (lease.property_name || "property").replace(/[^A-Za-z0-9_-]+/g, "_");
      const safeTenant = (lease.tenant_name || "tenant").replace(/[^A-Za-z0-9_-]+/g, "_");
      await authenticatedDownload(
        `/api/leases/${lease.id}/agreement/pdf`,
        `lease-draft-${safeProp}-${safeTenant}.pdf`,
      );
      toast.success("Preview downloaded", { id: `agreement-preview-${lease.id}` });
    } catch (e) {
      toast.error("Failed to generate PDF", { id: `agreement-preview-${lease.id}` });
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="lease-agreement-editor">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            Edit lease agreement
            {lease ? (
              <span className="block text-xs font-normal text-slate-500 mt-1">
                {lease.property_name} · {lease.unit_label} · {lease.tenant_name}
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {loading || !state ? (
            <div className="py-12 text-center text-sm text-slate-500" data-testid="agreement-loading">
              Loading agreement…
            </div>
          ) : (
            <>
              <ModeBadge state={state} />

              <div className="flex border-b border-slate-200 mt-4 mb-4 text-sm font-semibold">
                <TabBtn id="clauses" active={activeTab === "clauses"} onClick={() => setActiveTab("clauses")}>
                  Clause overrides
                  {state.clause_overrides && Object.keys(state.clause_overrides).length > 0 ? (
                    <span className="ml-1.5 text-[10px] bg-amber-100 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-sm">
                      {Object.keys(state.clause_overrides).length}
                    </span>
                  ) : null}
                </TabBtn>
                <TabBtn id="upload" active={activeTab === "upload"} onClick={() => setActiveTab("upload")}>
                  Upload custom PDF
                  {state.custom_pdf_filename ? (
                    <span className="ml-1.5 text-[10px] bg-violet-100 text-violet-800 border border-violet-200 px-1.5 py-0.5 rounded-sm">on</span>
                  ) : null}
                </TabBtn>
              </div>

              {activeTab === "clauses" ? (
                <ClauseEditor
                  state={state}
                  drafts={drafts}
                  setDrafts={setDrafts}
                  onClear={clearClause}
                  onRevert={revertClause}
                />
              ) : (
                <UploadPanel
                  state={state}
                  uploading={uploading}
                  fileInputRef={fileInputRef}
                  onUpload={onUploadPdf}
                  onRemove={removeCustomPdf}
                />
              )}
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-100 pt-4 mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={previewPdf}
            className="btn-outline-dark inline-flex items-center gap-2"
            data-testid="agreement-preview-button"
            disabled={loading || !state}
          >
            <FileText className="w-4 h-4" /> Preview PDF
          </button>
          <div className="flex-1" />
          <button type="button" onClick={onClose} className="btn-outline-dark" data-testid="agreement-close">
            Close
          </button>
          {activeTab === "clauses" ? (
            <button
              type="button"
              onClick={saveClauseOverrides}
              className="btn-primary-dark inline-flex items-center gap-2"
              disabled={!dirty || saving || loading}
              data-testid="agreement-save-clauses"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save overrides"}
            </button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabBtn({ active, onClick, children, id }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 -mb-px border-b-2 transition-colors ${active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
      data-testid={`agreement-tab-${id}`}
    >
      {children}
    </button>
  );
}

function ModeBadge({ state }) {
  const cfg = {
    auto: { label: "Auto-generated", cls: "bg-slate-100 text-slate-700 border-slate-200", note: "Using the standard auto-generated commercial lease for this lease type." },
    overridden: { label: "Custom clauses", cls: "bg-amber-50 text-amber-800 border-amber-200", note: "Auto-generated lease with one or more custom clauses applied." },
    uploaded: { label: "Custom PDF", cls: "bg-violet-50 text-violet-800 border-violet-200", note: "Using your uploaded PDF — auto-generated text and clause overrides are ignored." },
  }[state.mode] || { label: state.mode, cls: "bg-slate-100 text-slate-700 border-slate-200", note: "" };

  return (
    <div className={`mt-1 border ${cfg.cls} rounded-sm px-3 py-2 text-xs flex items-start gap-2`} data-testid="agreement-mode-badge">
      <span className="font-bold uppercase tracking-wider whitespace-nowrap">{cfg.label}</span>
      <span className="text-slate-600">{cfg.note}</span>
    </div>
  );
}

function ClauseEditor({ state, drafts, setDrafts, onClear, onRevert }) {
  const clauses = state.overridable_clauses || [];
  const overrideMap = state.clause_overrides || {};

  return (
    <div className="space-y-4" data-testid="clause-overrides-list">
      {state.mode === "uploaded" ? (
        <div className="bg-violet-50 border border-violet-200 rounded-sm p-3 text-xs text-violet-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            A custom PDF is currently uploaded. Clause overrides below are saved but <strong>not used</strong> until the uploaded PDF is removed (Upload tab → Remove).
          </span>
        </div>
      ) : null}
      <p className="text-xs text-slate-500">
        Leave a field blank to use the system default for that clause. Plain text is fine — line breaks are preserved.
      </p>

      {clauses.map((c) => {
        const draft = drafts[c.id] || "";
        const persisted = overrideMap[c.id] || "";
        const isDirty = draft !== persisted;
        return (
          <div key={c.id} className="border border-slate-200 rounded-sm p-3" data-testid={`clause-row-${c.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div>
                <div className="font-semibold text-sm text-slate-900" style={{ fontFamily: "Outfit" }}>
                  {c.title}
                  {persisted ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 rounded-sm">
                      Overridden
                    </span>
                  ) : null}
                  {isDirty ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-sky-100 text-sky-800 border border-sky-200 rounded-sm">
                      Unsaved
                    </span>
                  ) : null}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{c.hint}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                {isDirty ? (
                  <button
                    type="button"
                    onClick={() => onRevert(c.id)}
                    className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
                    title="Revert unsaved changes for this clause"
                    data-testid={`clause-revert-${c.id}`}
                  >
                    <RotateCcw className="w-3 h-3" /> Revert
                  </button>
                ) : null}
                {draft ? (
                  <button
                    type="button"
                    onClick={() => onClear(c.id)}
                    className="text-xs text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
                    title="Clear this override (uses system default)"
                    data-testid={`clause-clear-${c.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Clear
                  </button>
                ) : null}
              </div>
            </div>
            <textarea
              rows={4}
              maxLength={20000}
              className="input-clean w-full font-mono text-xs"
              placeholder="Leave blank to use the default clause text…"
              value={draft}
              onChange={(e) => setDrafts((d) => ({ ...d, [c.id]: e.target.value }))}
              data-testid={`clause-textarea-${c.id}`}
            />
            <div className="text-[10px] text-slate-400 text-right mt-0.5">
              {draft.length} / 20,000
            </div>
          </div>
        );
      })}
    </div>
  );
}

function UploadPanel({ state, uploading, fileInputRef, onUpload, onRemove }) {
  return (
    <div className="space-y-4" data-testid="upload-panel">
      <p className="text-xs text-slate-500">
        Replace the auto-generated lease with an attorney-drafted PDF. The uploaded file is what tenants will sign and what's served when downloading the agreement. Max 10 MB, PDF only.
      </p>

      {state.custom_pdf_filename ? (
        <div className="border border-violet-200 bg-violet-50/40 rounded-sm p-3 text-sm" data-testid="custom-pdf-current">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold text-slate-900 inline-flex items-center gap-2">
                <FileText className="w-4 h-4 text-violet-700" />
                {state.custom_pdf_filename}
              </div>
              {state.custom_pdf_uploaded_at ? (
                <div className="text-xs text-slate-500 mt-0.5">
                  Uploaded {new Date(state.custom_pdf_uploaded_at).toLocaleString()}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onRemove}
              className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1"
              data-testid="custom-pdf-remove"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-sm text-slate-500" data-testid="custom-pdf-empty">
          No custom PDF uploaded. Auto-generated agreement is in use.
        </div>
      )}

      <label className="btn-outline-dark inline-flex items-center gap-2 cursor-pointer" data-testid="custom-pdf-upload-label">
        <Upload className="w-4 h-4" />
        {uploading ? "Uploading…" : (state.custom_pdf_filename ? "Replace PDF" : "Upload PDF")}
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={onUpload}
          disabled={uploading}
          data-testid="custom-pdf-file-input"
        />
      </label>
    </div>
  );
}
